import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const root = window.Shopify?.routes?.root || '/';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const motion = () => reducedMotion.matches ? 'instant' : 'smooth';
const stripMoney = (value) => {
  const node = document.createElement('span');
  node.innerHTML = value || '';
  return node.textContent;
};
const updateCount = (count) => document.querySelectorAll('[data-mc-cart-count]').forEach(node => node.textContent = count);

class MiniProduct extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.data = JSON.parse(this.querySelector('[data-mc-product-data]').textContent);
    this.form = this.querySelector('form.mc-product-form');
    this.input = this.querySelector('[data-mc-variant-input]');
    this.quantity = this.querySelector('[data-mc-quantity]');
    this.card = this.querySelector('[data-mc-card]');
    this.gallery = this.querySelector('[data-mc-gallery]');
    this.track = this.querySelector('[data-mc-track]');
    this.slides = [...this.querySelectorAll('[data-mc-slide]')];
    this.index = 0;
    this.dataset.enhanced = '';
    this.input.addEventListener('change', () => this.selectVariant(this.input.value), { signal });
    this.querySelectorAll('[data-mc-variant]').forEach(button => button.addEventListener('click', () => {
      this.input.value = button.dataset.mcVariant;
      this.selectVariant(button.dataset.mcVariant);
    }, { signal }));
    this.querySelector('[data-mc-minus]').addEventListener('click', () => { this.quantity.stepDown(); }, { signal });
    this.querySelector('[data-mc-plus]').addEventListener('click', () => { this.quantity.stepUp(); }, { signal });
    this.card?.addEventListener('change', () => this.syncPayment(), { signal });
    this.form.addEventListener('submit', event => { event.preventDefault(); this.add(false); }, { signal });
    this.querySelector('[data-mc-buy]').addEventListener('click', () => this.add(true), { signal });
    this.querySelector('[data-mc-sticky-add]').addEventListener('click', () => this.add(false), { signal });
    this.querySelectorAll('[data-mc-media]').forEach(button => button.addEventListener('click', () => this.showMedia(button.dataset.mcMedia), { signal }));
    this.querySelector('[data-mc-prev]').addEventListener('click', () => this.showIndex(this.index - 1), { signal });
    this.querySelector('[data-mc-next]').addEventListener('click', () => this.showIndex(this.index + 1), { signal });
    this.track.addEventListener('keydown', event => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault(); this.showIndex(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    }, { signal });
    this.track.addEventListener('scroll', () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.markIndex(Math.round(this.track.scrollLeft / this.track.clientWidth));
      });
    }, { signal, passive: true });
    this.querySelectorAll('[data-mc-video]').forEach(button => {
      const video = button.closest('figure').querySelector('video');
      button.addEventListener('click', async () => {
        if (video.paused) {
          try { await video.play(); } catch { return; }
        } else video.pause();
      }, { signal });
      const update = () => {
        button.textContent = video.paused ? '▶' : 'Ⅱ';
        button.setAttribute('aria-pressed', String(!video.paused));
      };
      video.addEventListener('play', update, { signal });
      video.addEventListener('pause', update, { signal });
    });
    this.videoObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => { if (!entry.isIntersecting) entry.target.pause(); });
    }, { threshold: 0.1 });
    this.querySelectorAll('video').forEach(video => this.videoObserver.observe(video));
    this.stickyObserver = new IntersectionObserver(() => this.syncSticky(), { threshold: 0 });
    this.stickyObserver.observe(this.form);
    window.addEventListener('scroll', () => {
      if (this.stickyFrame) return;
      this.stickyFrame = requestAnimationFrame(() => { this.stickyFrame = 0; this.syncSticky(); });
    }, { signal, passive: true });
    this.resizeObserver = new ResizeObserver(() => this.showIndex(this.index, 'instant'));
    this.resizeObserver.observe(this.track);
    this.selectVariant(this.input.value, false);
    if (this.slides.some(slide => slide.dataset.mcSlide === 'editorial')) this.showMedia('editorial', 'instant');
    else this.showMedia(this.gallery.dataset.mainMedia, 'instant');
    this.syncPayment();
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.videoObserver?.disconnect();
    this.stickyObserver?.disconnect();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.stickyFrame);
    cancelAnimationFrame(this.scrollFrame);
  }
  selectVariant(id, updateUrl = true) {
    const variant = this.data.variants.find(v => String(v.id) === String(id));
    if (!variant) return;
    this.variant = variant;
    this.querySelectorAll('[data-mc-variant]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mcVariant === String(id))));
    const color = this.querySelector('[data-mc-color]');
    if (color) color.textContent = variant.title;
    this.querySelector('[data-mc-price]').textContent = stripMoney(variant.money);
    this.querySelector('[data-mc-sticky-price]').textContent = stripMoney(variant.money);
    const compare = this.querySelector('[data-mc-compare]');
    const saving = this.querySelector('[data-mc-saving]');
    compare.hidden = saving.hidden = !(variant.compare > variant.price);
    compare.textContent = stripMoney(variant.compareMoney);
    saving.textContent = this.data.text.save + ' ' + Math.round((variant.compare - variant.price) / variant.compare * 100) + '%';
    this.querySelector('[data-mc-add]').disabled = !variant.available;
    this.querySelector('[data-mc-buy]').disabled = !variant.available;
    this.querySelector('[data-mc-sticky-add]').disabled = !variant.available;
    this.querySelector('[data-mc-add-label]').textContent = variant.available ? this.data.text.add + ' — ' + stripMoney(variant.money) : this.data.text.sold;
    this.quantity.min = variant.min;
    this.quantity.step = variant.step;
    if (variant.max) this.quantity.max = variant.max; else this.quantity.removeAttribute('max');
    if (!this.quantity.checkValidity()) this.quantity.value = variant.min;
    const stickyImage = this.querySelector('[data-mc-sticky] img');
    if (stickyImage && variant.image) {
      stickyImage.removeAttribute('srcset');
      stickyImage.src = variant.image;
    }
    this.showMedia(variant.media, updateUrl ? motion() : 'instant');
    if (updateUrl && document.querySelector('main[data-template^="product"]')) {
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }
  }
  syncPayment() {
    const includeCard = Boolean(this.card?.checked);
    this.querySelector('[data-mc-payment]').hidden = includeCard || !this.variant.available;
    this.querySelector('[data-mc-buy]').hidden = !includeCard;
  }
  showMedia(id, behavior = motion()) {
    const index = this.slides.findIndex(slide => slide.dataset.mcSlide === String(id));
    if (index >= 0) this.showIndex(index, behavior);
  }
  showIndex(index, behavior = motion()) {
    index = Math.max(0, Math.min(this.slides.length - 1, index));
    this.track.scrollTo({ left: index * this.track.clientWidth, behavior });
    this.markIndex(index);
  }
  markIndex(index) {
    this.index = Math.max(0, Math.min(this.slides.length - 1, index));
    const id = this.slides[this.index]?.dataset.mcSlide;
    this.querySelectorAll('[data-mc-media]').forEach(button => button.setAttribute('aria-current', String(button.dataset.mcMedia === id)));
    this.querySelector('[data-mc-prev]').disabled = this.index === 0;
    this.querySelector('[data-mc-next]').disabled = this.index === this.slides.length - 1;
    this.slides.forEach((slide, i) => { if (i !== this.index) slide.querySelector('video')?.pause(); });
  }
  syncSticky() {
    this.querySelector('[data-mc-sticky]').hidden = this.form.getBoundingClientRect().bottom >= 0;
  }
  async add(checkout) {
    if (this.busy || !this.variant.available || !this.form.reportValidity()) return;
    this.busy = true;
    const controls = [...this.querySelectorAll('button,input,select')].map(node => [node, node.disabled]);
    controls.forEach(([node]) => node.disabled = true);
    const status = this.querySelector('[data-mc-status]');
    const cartLink = this.querySelector('[data-mc-cart-link]');
    status.textContent = this.data.text.adding;
    cartLink.hidden = true;
    const items = [{ id: Number(this.input.value), quantity: Number(this.quantity.value) }];
    if (this.card?.checked) items.push({ id: Number(this.card.value), quantity: 1 });
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(node => node.dataset.sectionId).filter(Boolean))].slice(0,5);
    const promise = (async () => {
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: location.pathname })
      });
      const payload = await response.json();
      const cartResponse = await fetch(root + 'cart.js', { cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('Cart confirmation unavailable');
      const cart = await cartResponse.json();
      updateCount(cart.item_count);
      if (!response.ok || payload.status) {
        return { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, itemCount: cart.item_count, sections: payload.sections || {}, source: 'nomvia-mini-cam', didError: true, message: payload.description || payload.message } };
      }
      return { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, sections: payload.sections || {}, source: 'nomvia-mini-cam', didError: false } };
    })();
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product',
      lines: items.map(item => ({ merchandiseId: String(item.id), quantity: item.quantity })), promise }));
    try {
      const result = await promise;
      if (result.detail.didError) throw new Error(result.detail.message);
      status.textContent = this.data.text.added;
      if (checkout) location.assign(root + 'checkout');
    } catch {
      status.textContent = this.data.text.error;
      cartLink.hidden = false;
    } finally {
      controls.forEach(([node, disabled]) => node.disabled = disabled);
      this.busy = false;
    }
  }
}
if (!customElements.get('nomvia-mini-product')) customElements.define('nomvia-mini-product', MiniProduct);

document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  event.promise?.then(({ detail }) => {
    if (Array.isArray(detail?.items)) updateCount(detail.items.reduce((total, item) => total + Number(item.quantity || 0), 0));
    else fetch(root + 'cart.js', { cache: 'no-store' }).then(r => r.json()).then(cart => updateCount(cart.item_count)).catch(() => {});
  }).catch(() => {});
});

const enhanced = new WeakSet();
function enhance(scope = document) {
  const menu = scope.querySelector('[data-mc-menu]');
  if (menu && !enhanced.has(menu)) {
    enhanced.add(menu);
    scope.querySelector('[data-mc-menu-open]')?.addEventListener('click', () => {
      menu.showModal(); menu.dataset.previousOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden';
    });
    const close = () => menu.close();
    menu.querySelector('[data-mc-menu-close]').addEventListener('click', close);
    menu.querySelectorAll('a').forEach(link => link.addEventListener('click', close));
    menu.addEventListener('click', event => { if (event.target === menu && event.clientX > menu.getBoundingClientRect().right) close(); });
    menu.addEventListener('close', () => { document.body.style.overflow = menu.dataset.previousOverflow || ''; });
  }
  scope.querySelectorAll('[data-mc-cart]').forEach(link => {
    if (enhanced.has(link)) return;
    enhanced.add(link);
    link.addEventListener('click', event => {
      const drawer = document.querySelector('#cart-drawer');
      if (typeof drawer?.open === 'function') { event.preventDefault(); drawer.open(); }
    });
  });
  scope.querySelectorAll('[data-mc-accordion]').forEach(details => {
    if (enhanced.has(details)) return;
    enhanced.add(details);
    const summary = details.querySelector('summary');
    const sync = () => {
      summary.setAttribute('aria-expanded', String(details.open));
      summary.querySelector('span').textContent = details.open ? '−' : '+';
    };
    sync();
    details.addEventListener('toggle', () => { if (!details.mcAnimation) sync(); });
    summary.addEventListener('click', event => {
      event.preventDefault();
      const from = details.getBoundingClientRect().height;
      const opening = details.mcTarget === undefined ? !details.open : !details.mcTarget;
      details.mcTarget = opening;
      details.mcAnimation?.cancel();
      details.open = true;
      const border = parseFloat(getComputedStyle(details).borderTopWidth) + parseFloat(getComputedStyle(details).borderBottomWidth);
      const to = opening ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height + border;
      summary.setAttribute('aria-expanded', String(opening));
      summary.querySelector('span').textContent = opening ? '−' : '+';
      details.style.overflow = 'hidden';
      const animation = details.animate([{ height: from + 'px' }, { height: to + 'px' }], { duration: reducedMotion.matches ? 0 : 210, easing: 'ease-out' });
      details.mcAnimation = animation;
      animation.onfinish = () => {
        details.open = opening;
        details.style.overflow = '';
        details.mcAnimation = null;
        details.mcTarget = undefined;
        sync();
      };
    });
  });
}
enhance();
if (!reducedMotion.matches) {
  const reveal = new IntersectionObserver(entries => entries.forEach(entry => {
    if (!entry.isIntersecting) return;
    entry.target.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 350, easing: 'ease-out' });
    reveal.unobserve(entry.target);
  }), { threshold: 0.08 });
  document.querySelectorAll('[data-mc-reveal]').forEach(section => reveal.observe(section));
}
document.addEventListener('shopify:section:load', event => enhance(event.target));
